import { effect, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppInformationService } from '../app-information.service';
import { environment } from '../../../environments/environment';
import { Response } from '../../../app/core/response';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class UtilityService {
    static serviceName: string;
    static functionName: any;
    registrationCount = signal<number>(0);
    customerReviewCount = signal<number>(0);

    constructor(
        private http: HttpClient,
        private appInfo: AppInformationService,
    ) {
        UtilityService.serviceName = environment.serviceName;
        UtilityService.functionName = environment.functionName;

        effect(async () => {
            this.registrationCount.set((await this.getRegistrationCount()) as any);
        });

        effect(async () => {
            this.customerReviewCount.set((await this.getCustomerReviewCount()) as any);
        });
    }

    // leave this as async so it doesn't hang up anything
    async getServiceVersion() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getServiceVersion`, {}).toPromise();
        return result;
    }

    async getFeatureFlags() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getFeatureFlags`, {}).toPromise();
        return result as { dealerDeliveryConfirmationEnabled: boolean };
    }

    async getNextOrderNumber() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getNextOrderNumber`, {}).toPromise();
        return result;
    }
    async getNextSalesOrderNumber() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getNextSalesOrderNumber`, {}).toPromise();
        return result;
    }
    async getNextInvoiceNumber() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getNextInvoiceNumber`, {}).toPromise();
        return result;
    }
    async getNextPackingSlipNumber() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getNextPackingSlipNumber`, {}).toPromise();
        return result;
    }
    async getNextOrderColor() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getNextOrderColor`, {}).toPromise();
        return result;
    }
    async submitOrder(orderId?: number) {
        const result = await this.http.get(`${UtilityService.functionName}/api/OrderLineDetailPartUpdate`).toPromise();
        return result;
    }

    async completeOrders() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/completeOrders`, {}).toPromise();
        return result;
    }

    async calculateOrderTotal(orderId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/calculateOrderTotal`, { orderId }).toPromise();
        return result;
    }
    async deleteOrderLineDetail(orderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/deleteOrderLineDetail`, { orderLineDetailId }).toPromise();
        return result;
    }
    async unBuildOrderLineDetail(orderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/unBuildOrderLineDetail`, { orderLineDetailId }).toPromise();
        return result;
    }
    async duplicateVins(vins: any[], orderLineDetailIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/duplicateVins`, { vins, orderLineDetailIds }).toPromise();
        return result;
    }
    async getOpenOrdersCount(partLinkId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getOpenOrderLineDetailPartsCount`, { partLinkId }).toPromise();
        return result;
    }

    async getOpenOrderLineDetailIds(partLinkId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getOpenOrderLineDetailIds`, { partLinkId }).toPromise();
        return result;
    }

    async editOrDeletePartLinks(partLinkId: number, newQuantity: number, deleteLink?: boolean) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/editOrDeletePartLinkQuantities`, { partLinkId, newQuantity, deleteLink }).toPromise();
        return result;
    }
    async addPartLinks(detailIds: number[], partLinkIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/addPartLink`, { orderLineDetailIds: detailIds, partLinkIds }).toPromise();
        return result;
    }

    async processOrderLineDetails(orderLineDetailIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/processOrderLineDetail`, { orderLineDetailIds }).toPromise();
        return result;
    }

    async processBuildAssemblyDetails(buildAssemblyDetailIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/processBuildAssemblyDetails`, { buildAssemblyDetailIds }).toPromise();
        return result;
    }

    async editOrderLineDetailQuantity(partLinkId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/editOrderLineDetailQuantity`, { partLinkId }).toPromise();
        return result;
    }

    async setOrderComplete(orderIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/setOrderForComplete`, { orderIds }).toPromise();
        return result;
    }

    async setOrdersCompleteByInvoiceIds(invoiceIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/setOrdersCompleteByInvoiceIds`, { invoiceIds: invoiceIds }).toPromise();
        return result;
    }

    // async emailSalesOrder(id: number) {
    //     const result = await this.http.post(`${UtilityService.serviceName}/utility/emailOrder`, { orderId: id }).toPromise();
    //     return result;
    // }
    async emailReport(id: number) {
        try {
            const result = await this.http.post(`${UtilityService.serviceName}/utility/emailReport`, { emailId: id }).toPromise();
            return result as boolean;
        } catch (error) {
            console.error('Error emailing report', error);
            return false;
        }
    }
    async getCalculatedCost() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getCalculatedCost`, {}).toPromise();
        return result;
    }
    async startCalculatedCostJob(): Promise<{ jobId: string }> {
        return await this.http.post<{ jobId: string }>(`${UtilityService.serviceName}/utility/startCalculatedCostJob`, {}).toPromise();
    }
    async getCalculatedCostProgress(jobId: string): Promise<{ processed: number; total: number; currentPartNumber: string; status: string; errorMessage: string }> {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/getCalculatedCostProgress`, { jobId }).toPromise();
    }
    async updateInventory(orderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/updateInventory`, { orderLineDetailId: orderLineDetailId }).toPromise();
        return result;
    }
    async getInventoryAdjustments() {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/getLastPartInventoryAdjustment`, {}).toPromise();
        return result;
    }
    async restockCreditOrderLineDetail(orderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/restockCreditOrderLineDetail`, { orderLineDetailId: orderLineDetailId }).toPromise();
        return result;
    }

    async allocatedStockToOrder(stockOrderLineDetailId: number, selectedOrderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/allocatedStockToOrder`, { stockOrderLineDetailId, selectedOrderLineDetailId }).toPromise();
        return result;
    }

    async addToStock(selectedOrderLineDetailId: number, userId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/addToStockFromOrder`, { selectedOrderLineDetailId, userId }).toPromise();
        return result;
    }

    async manualUpdateInventory(orderLineDetailId: number) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/manualUpdateInventory`, { orderLineDetailId }).toPromise();
        return result;
    }

    async emailDealerEntry(dealerId: number) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/utility/emailDealerEntry`, { id: dealerId }).toPromise();
    }

    async multipleOrderLineQuantityCheck(detailId: number) {
        return await this.http.post<boolean>(`${UtilityService.serviceName}/utility/multipleOrderLineQuantityCheck`, { detailId }).toPromise();
    }
    async splitOrderLinesBasedOnDetailEdit(detailId: number) {
        return await this.http.post<boolean>(`${UtilityService.serviceName}/utility/splitOrderLinesBasedOnDetailEdit`, { detailId }).toPromise();
    }
    async checkForPayments(detailIds: number[]) {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/checkForPayments`, { detailIds }).toPromise();
    }
    async setPaymentLinePackSlipId(detailIds: number[], packingSlipId: number) {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/setPaymentLinePackSlipId`, { detailIds, packingSlipId }).toPromise();
    }
    async paymentACHCheck(detailIds: number[]) {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/paymentACHCheck`, { detailIds }).toPromise();
    }
    async checkForOpenSalesOrderFees(invoiceId: number) {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/checkForOpenSalesOrderFees`, { invoiceId }).toPromise();
    }
    async uploadImageData(id: number, imageData: string, mode: 'Invoice' | 'PackingSlip' | 'SalesOrder' | 'PurchaseOrder' | 'Delivery') {
        return await this.http.post<any>(`${UtilityService.serviceName}/file/uploadImageData`, { id, imageData, mode }).toPromise();
    }
    async getDetailIdsOnAStack(orderLineDetailIds: number[]) {
        return await this.http.post<number[]>(`${UtilityService.serviceName}/utility/getDetailIdsOnAStack`, { orderLineDetailIds }).toPromise();
    }

    async deleteOrderLineDetailPartsWhenSwitchingSalesOrderStatus(orderLineDetailIds: number[]) {
        return await this.http
            .post<any>(`${UtilityService.serviceName}/utility/deleteOrderLineDetailPartsWhenSwitchingSalesOrderStatus`, { orderLineDetailIds: orderLineDetailIds })
            .toPromise();
    }

    async removeOrderLineAfterSubmitted(orderLineId: number) {
        return await this.http.post<any>(`${UtilityService.serviceName}/utility/removeOrderLineAfterSubmitted`, { orderLineId: orderLineId }).toPromise();
    }

    async areOrderLineDetailsOrOptionsBuilt(orderLineDetailIds: number[]) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/utility/areOrderLineDetailsOrOptionsBuilt`, { orderLineDetailIds }).toPromise();
    }

    async checkAchPaymentConfirmationEmail(achId: number) {
        return await this.http.post<boolean>(`${UtilityService.serviceName}/ach/checkAchPaymentConfirmationEmail`, { achId }).toPromise();
    }

    async sendAchPaymentConfirmation(achId: number) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/ach/sendAchPaymentConfirmation`, { achId }).toPromise();
    }

    async sendOpenAchPaymentReceivedAlert(paymentId: number, achRemoved: boolean) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/ach/sendOpenAchPaymentReceivedAlert`, { paymentId, achRemoved }).toPromise();
    }

    async sendEmail(emailId: number) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/utility/sendEmail`, { emailId }).toPromise();
    }

    async sendAchLinkEmailWithInvoices(emailId: number, achId: number) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/utility/sendAchLinkEmailWithInvoices`, { emailId, achId }).toPromise();
    }

    async processCustomerFeedback(reviewId: number) {
        return await this.http.post<Response>(`${UtilityService.serviceName}/webhook/processCustomerFeedback`, { reviewId }).toPromise();
    }

    async getRegistrationCount() {
        return await this.http.post<Response>(`${UtilityService.serviceName}/webhook/getRegistrationCount`, {}).toPromise();
    }

    async getCustomerReviewCount() {
        return await this.http.post<Response>(`${UtilityService.serviceName}/webhook/getCustomerReviewCount`, {}).toPromise();
    }

    async onTimeBuildOverview(start: Date, end: Date, basedOnDate: boolean) {
        const result = await firstValueFrom<any>(
            this.http.post(`${UtilityService.serviceName}/utility/onTimeBuildOverview`, { startDate: start, endDate: end, basedOnDate: basedOnDate }),
        );
        return result;
    }

    async onTimeDeliveryOverview(start: Date, end: Date, basedOnDate: boolean) {
        const result = await firstValueFrom<any>(
            this.http.post(`${UtilityService.serviceName}/utility/onTimeDeliveryOverview`, { startDate: start, endDate: end, basedOnDate: basedOnDate }),
        );
        return result;
    }

    async processPartConfigurationAttachments() {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/file/processPartConfigurationAttachments`, {}));
        return result;
    }

    async processPartConfigurations(configIds: number[], user: string) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/processPartConfigurations`, { configIds, user }).toPromise();
        return result;
    }

    async updatePartConfigurations(configIds: number[]) {
        const result = await this.http.post(`${UtilityService.serviceName}/utility/updatePartConfigurations`, { configIds }).toPromise();
        return result;
    }

    async getNextInventoryProjectionNumber() {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/getNextInventoryProjectionNumber`, {}));
        return result;
    }
    async processInventoryProjection(inventoryProjectionId: number, includeAssemblyParts = false) {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/ProcessInventoryProjection`, { inventoryProjectionId, includeAssemblyParts }));
        return result;
    }

    async deleteInventoryProjection(inventoryProjectionId: number) {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/deleteInventoryProjection`, { inventoryProjectionId }));
        return result;
    }

    async updateReceivingQuickBooksItem(purchaseOrderReceivingId: number, quickBooksItemId: number) {
        const result = await firstValueFrom<any>(
            this.http.post(`${UtilityService.serviceName}/utility/updateReceivingQuickBooksItem`, { purchaseOrderReceivingId, quickBooksItemId }),
        );
        return result;
    }

    async createMsoOrder(dealerId: number, msoPrice: number, vins: string[], userId: number) {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/createMsoOrder`, { dealerId, msoPrice, vins, userId }));
        return result;
    }
}
